import React, { useEffect, useState } from "react";

function statusLabel(status) {
  if (status === "assigned") return "Assigned";
  if (status === "already scheduled") return "Already scheduled";
  if (status === "unavailable") return "No availability";
  return "Not selected";
}

export default function SolverPage() {
  const [families, setFamilies] = useState([]);
  const [familyId, setFamilyId] = useState("");
  const [preferSlot, setPreferSlot] = useState("ANY");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applyStatus, setApplyStatus] = useState("");
  const [guestApplyStatus, setGuestApplyStatus] = useState({});
  const [error, setError] = useState("");
  const [showOnlyUnscheduled, setShowOnlyUnscheduled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadFamilies() {
      setError("");
      const res = await fetch("/api/families");
      const json = await res.json();
      if (!res.ok) {
        if (!cancelled) setError(json?.error || "Failed to load families");
        return;
      }
      if (!cancelled) setFamilies(json || []);
    }
    loadFamilies();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPreview(null);
    setApplyStatus("");
  }, [familyId, preferSlot]);

  async function runPreview() {
    if (!familyId) return;
    setLoading(true);
    setError("");
    setApplyStatus("");
    try {
      const res = await fetch(`/api/solver/preview?familyId=${familyId}&preferSlot=${preferSlot}`);
      const json = await res.json();
      if (!res.ok) {
        setError(json?.error || "Failed to generate preview");
        return;
      }
      setPreview(json);
    } finally {
      setLoading(false);
    }
  }

  async function applyAssignments() {
    if (!preview?.assignments?.length) return;
    setLoading(true);
    setError("");
    setApplyStatus("");
    setGuestApplyStatus({});
    try {
      const res = await fetch("/api/solver/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assignments: preview.assignments }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json?.error || "Failed to apply assignments");
        return;
      }
      setApplyStatus(`Applied ${json?.updated || 0} schedule updates.`);
      await runPreview();
    } finally {
      setLoading(false);
    }
  }

  async function applyGuestAssignments(guestId) {
    if (!guestId || !preview?.assignments?.length) return;
    const guestAssignments = preview.assignments.filter((a) => a.guestId === guestId);
    if (!guestAssignments.length) {
      setGuestApplyStatus((prev) => ({ ...prev, [guestId]: "No assignments for this guest." }));
      return;
    }
    setLoading(true);
    setError("");
    setApplyStatus("");
    setGuestApplyStatus((prev) => ({ ...prev, [guestId]: "Applying…" }));
    try {
      const res = await fetch("/api/solver/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assignments: guestAssignments }),
      });
      const json = await res.json();
      if (!res.ok) {
        setGuestApplyStatus((prev) => ({
          ...prev,
          [guestId]: json?.error || "Failed to apply assignments",
        }));
        return;
      }
      setGuestApplyStatus((prev) => ({
        ...prev,
        [guestId]: `Applied ${json?.updated || 0} updates.`,
      }));
      await runPreview();
    } finally {
      setLoading(false);
    }
  }

  const summary = preview?.summary;
  const assignmentsCount = preview?.assignments?.length || 0;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: 20 }}>
      <style>{`
        @media (max-width: 700px) {
          .solver-controls {
            grid-template-columns: 1fr !important;
          }
          .solver-card {
            padding: 12px !important;
          }
        }
      `}</style>

      <div style={{ fontSize: 22, fontWeight: 800 }}>Schedule Optimizer</div>
      <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
        Generate a proposed schedule for a family based on their top 10 activity choices.
      </div>

      <div
        className="solver-controls"
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns: "1fr 220px 160px",
          gap: 12,
          alignItems: "end",
        }}
      >
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontWeight: 700 }}>Family/Group</span>
          <select value={familyId} onChange={(e) => setFamilyId(e.target.value)}>
            <option value="">Select a family…</option>
            {families.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name || "(Unnamed family)"}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontWeight: 700 }}>AM/PM preferred</span>
          <select value={preferSlot} onChange={(e) => setPreferSlot(e.target.value)}>
            <option value="ANY">No preference</option>
            <option value="AM">Prefer AM</option>
            <option value="PM">Prefer PM</option>
          </select>
        </label>

        <button onClick={runPreview} disabled={!familyId || loading} style={{ height: 38 }}>
          {loading ? "Working…" : "Generate Proposal"}
        </button>
      </div>

      {error ? <div style={{ marginTop: 12, color: "crimson" }}>{error}</div> : null}

      {preview ? (
        <div style={{ marginTop: 20, display: "grid", gap: 16 }}>
          <div
            className="solver-card"
            style={{
              border: "1px solid var(--stroke)",
              borderRadius: 12,
              padding: 16,
              background: "var(--card)",
              boxShadow: "0 10px 24px rgba(32, 65, 81, 0.12)",
            }}
          >
            <div style={{ fontWeight: 800 }}>{preview.familyName || "Family"}</div>
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
              Proposed assignments for {preview.guests?.length || 0} guests.
            </div>
            {summary ? (
              <div style={{ marginTop: 12, display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div><strong>{summary.assigned}</strong> assigned</div>
                <div><strong>{summary.alreadyScheduled}</strong> already scheduled</div>
                <div><strong>{summary.unavailable}</strong> unavailable</div>
                <div><strong>{summary.empty}</strong> empty</div>
              </div>
            ) : null}
            <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={showOnlyUnscheduled}
                  onChange={(e) => setShowOnlyUnscheduled(e.target.checked)}
                />
                <span style={{ fontSize: 12, fontWeight: 700 }}>
                  Show only unscheduled choices
                </span>
              </label>
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <button onClick={applyAssignments} disabled={!assignmentsCount || loading}>
                {loading ? "Applying…" : `Apply ${assignmentsCount} assignments`}
              </button>
              {applyStatus ? <span style={{ fontSize: 12, opacity: 0.7 }}>{applyStatus}</span> : null}
            </div>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            {(preview.guests || []).map((guest) => {
              const visibleChoices = showOnlyUnscheduled
                ? (guest.choices || []).filter((choice) => choice.status === "unavailable")
                : (guest.choices || []);
              if (showOnlyUnscheduled && visibleChoices.length === 0) return null;
              return (
              <div
                key={guest.id}
                className="solver-card"
                style={{
                  border: "1px solid var(--stroke)",
                  borderRadius: 12,
                  padding: 16,
                  background: "var(--card)",
                  boxShadow: "0 10px 24px rgba(32, 65, 81, 0.12)",
                }}
              >
                <div style={{ fontWeight: 800 }}>{guest.name || "Guest"}</div>
                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
                  {guest.arrivalDate && guest.departureDate
                    ? `Stay: ${guest.arrivalDate} to ${guest.departureDate}`
                    : "Stay dates not set"}
                </div>
                <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <button onClick={() => applyGuestAssignments(guest.id)} disabled={loading}>
                    Apply assignments for {guest.name || "guest"}
                  </button>
                  {guestApplyStatus[guest.id] ? (
                    <span style={{ fontSize: 12, opacity: 0.7 }}>{guestApplyStatus[guest.id]}</span>
                  ) : null}
                </div>
                <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                  {visibleChoices.map((choice) => (
                    <div
                      key={`${guest.id}-${choice.rank}`}
                      style={{
                        border: "1px solid var(--stroke)",
                        borderRadius: 10,
                        padding: 10,
                        background: "var(--card)",
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>
                        Choice {choice.rank}: {choice.activityName || "No activity selected"}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                        {statusLabel(choice.status)}
                        {choice.date && choice.slot
                          ? ` • ${choice.date} ${choice.slot}${choice.startTime ? ` (${choice.startTime})` : ""}`
                          : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
